import { useState, useCallback, useMemo } from 'react'
import { X, Loader2, CheckCircle2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { ImageUploadDropzone } from './ImageUploadDropzone'
import { useBulkUploadImages, type BulkUploadItem } from '@/hooks/useImages'
import { compressImages } from '@/utils/imageCompression'

interface BulkUploadImagesModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

interface PendingFile {
  id: string
  file: File
  name: string
  description: string
  previewUrl: string
}

export function BulkUploadImagesModal({
  open,
  onOpenChange,
}: BulkUploadImagesModalProps) {
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([])
  const [uploadComplete, setUploadComplete] = useState(false)
  const [compressProgress, setCompressProgress] = useState<{
    done: number
    total: number
  } | null>(null)
  const bulkUpload = useBulkUploadImages()
  const isBusy = bulkUpload.isPending || compressProgress !== null

  const handleFilesSelected = useCallback((files: File[]) => {
    const newFiles: PendingFile[] = files.map(file => ({
      id: crypto.randomUUID(),
      file,
      name: file.name.replace(/\.[^.]+$/, ''),
      description: '',
      previewUrl: URL.createObjectURL(file),
    }))
    setPendingFiles(prev => [...prev, ...newFiles].slice(0, 100))
  }, [])

  const removeFile = useCallback((id: string) => {
    setPendingFiles(prev => {
      const removed = prev.find(f => f.id === id)
      if (removed) URL.revokeObjectURL(removed.previewUrl)
      return prev.filter(f => f.id !== id)
    })
  }, [])

  const updateFile = useCallback(
    (id: string, field: 'name' | 'description', value: string) => {
      setPendingFiles(prev =>
        prev.map(f => (f.id === id ? { ...f, [field]: value } : f))
      )
    },
    []
  )

  const handleUpload = useCallback(async () => {
    setCompressProgress({ done: 0, total: pendingFiles.length })
    const compressed = await compressImages(
      pendingFiles.map(f => f.file),
      (done, total) => setCompressProgress({ done, total })
    )
    setCompressProgress(null)

    const items: BulkUploadItem[] = pendingFiles.map((f, i) => ({
      file: compressed[i],
      name: f.name || f.file.name,
      description: f.description || undefined,
    }))

    await bulkUpload.mutateAsync(items)
    setUploadComplete(true)
  }, [pendingFiles, bulkUpload])

  const handleClose = useCallback(() => {
    pendingFiles.forEach(f => URL.revokeObjectURL(f.previewUrl))
    setPendingFiles([])
    setUploadComplete(false)
    setCompressProgress(null)
    bulkUpload.reset()
    onOpenChange(false)
  }, [pendingFiles, onOpenChange, bulkUpload])

  const canUpload = useMemo(
    () => pendingFiles.length > 0 && pendingFiles.every(f => f.name.trim()),
    [pendingFiles]
  )

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-3xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Upload Images</DialogTitle>
        </DialogHeader>

        {uploadComplete ? (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <CheckCircle2 className="h-12 w-12 text-green-500" />
            <p className="text-lg font-medium">Upload Complete</p>
            <p className="text-sm text-muted-foreground">
              {pendingFiles.length} image{pendingFiles.length !== 1 ? 's' : ''}{' '}
              uploaded successfully
            </p>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto space-y-4">
            {/* Dropzone */}
            <ImageUploadDropzone
              onFilesSelected={handleFilesSelected}
              maxFiles={100 - pendingFiles.length}
              disabled={isBusy || pendingFiles.length >= 100}
            />

            {/* File Preview List */}
            {pendingFiles.length > 0 && (
              <div className="space-y-3">
                <p className="text-sm font-medium text-foreground">
                  {pendingFiles.length} file
                  {pendingFiles.length !== 1 ? 's' : ''} selected
                </p>
                <div className="space-y-2 max-h-[350px] overflow-y-auto">
                  {pendingFiles.map(pf => (
                    <div
                      key={pf.id}
                      className="flex items-start gap-3 rounded-lg border border-border p-3"
                    >
                      <img
                        src={pf.previewUrl}
                        alt={pf.name}
                        className="h-16 w-16 rounded object-cover shrink-0"
                      />
                      <div className="flex-1 space-y-2 min-w-0">
                        <Input
                          value={pf.name}
                          onChange={e =>
                            updateFile(pf.id, 'name', e.target.value)
                          }
                          placeholder="Image name"
                          className="h-8 text-sm"
                          disabled={isBusy}
                        />
                        <Textarea
                          value={pf.description}
                          onChange={e =>
                            updateFile(pf.id, 'description', e.target.value)
                          }
                          placeholder="Description (optional)"
                          className="text-xs resize-none"
                          rows={1}
                          disabled={isBusy}
                        />
                      </div>
                      <button
                        onClick={() => removeFile(pf.id)}
                        className="shrink-0 cursor-pointer"
                        disabled={isBusy}
                      >
                        <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {uploadComplete ? (
            <Button onClick={handleClose}>Close</Button>
          ) : (
            <>
              <Button variant="outline" onClick={handleClose} disabled={isBusy}>
                Cancel
              </Button>
              <Button
                onClick={handleUpload}
                disabled={!canUpload || isBusy}
                className="cursor-pointer"
              >
                {compressProgress ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Compressing {compressProgress.done}/{compressProgress.total}
                    ...
                  </>
                ) : bulkUpload.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  `Upload ${pendingFiles.length} Image${pendingFiles.length !== 1 ? 's' : ''}`
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
